var _ = require("lodash");

var config = {};

config.Processes={
  showSeqRest: false,
  checkTypes: false,
  reactionStateLog: false,
  getFormReactionStateLog: false,
  checkingStateAndProcess: true,
  RefTextLog: false,
  replaceFormatedStringLog: false,
  logMsgInterpretation: true
};

/**
 * Process interpreter: the default Game.
 *
 * SUM FORMAT AGREEMENT:
 *
 * Let A[1], A[2], ..., A[k] be sets and A[1]+A[2]+...+A[n] be disjoint sum.
 * If `x` is an element of A[k], its representation in the disjoint sum is
 * `{ mark: k, value: x }`.
 * @module Processes
 */

var
  { ObjectId, StringId, FunctionId, OptionalId, TypedArrayId, UntypedArrayId, IdError, WrappedIdError, Identification, FormatTypeId, typeUnionId, UnionId, typeIdByPredicate } = require("../typing/typing"),
  Forms       = require("./Forms"),
  EmptyForm   = Forms.EmptyForm,
  InputOption = Forms.InputOption,
  SumForm     = Forms.SumForm,
  LockedForm  = Forms.LockedForm,
  Messages    = require("./Messages"),
  MessageId   = ObjectId,            // stub
  RefText     = require("./RefText"),
  RefTextId   = RefText.RefTextId,
  RefTextValue   = require("./RefTextValue").default,
  StatefulExpr   = require("./comp/StatefulExpr"),
  StatefulExprId = StatefulExpr.StatefulExprId,
	Lambda         = require("./comp/Lambda"),
	C = Lambda.Constructos,
	Types          = require("./comp/Types"),
	FnType = Types.FunctionType,
  Lists          = require("./comp/EInstances/Lists"),
  MultivariateFunction = require("./comp/EInstances/MultivariateFunction"),
	flattenTrans         = require("./flattenTrans"),
	simplifyProcess = require("./simplifyProcess"),
	isProcessEmpty  = require("./isProcessEmpty");

var smartDeepMap = require("../smartDeepMap");

// due to eager semantic of JS function calling,
// ProcessId must be declared before all specific parts (tp)
/**
 * Identification function of Process type.
 * @member
 */
function ProcessId(process) {
  if(!config.Processes.checkTypes) return process;
  if(!process)
    throw new IdError(process + " is not an object", process, ProcessId);
  if(process.type === undefined)
    throw new IdError("undefined type", process, ProcessId);
  var tp0 = get_tp();
  if(!tp0[process.type]) {
    console.log(process);
    console.warn("unknown process type " + process.type);
    return process;
  }
  try {
    return tp0[process.type](process);
  } catch(e) {
    var msg = "Process with " + process.type + " type does not match its id";
    throw new WrappedIdError(e, msg, process, ProcessId);
  }
};
ProcessId.typeName = "Process";
ProcessId = Identification(ProcessId);

// cloning state and logging it to the console
function logState(state) {
  console.log(_.assign(_.cloneDeep(state), { process: cloneProcess(state.process) }));
}

/**
 * verify a state to be appropriate for `getFormReaction`.
 * `state` must be an object with `process` field that has
 * a `getFormReaction` instance (see `exc` object).
 * @param {State} state
 * @return nothing
 * @throws when the state cannot be used in `getFormReaction`.
 * @private
 */
function checkStateAndProcess(state) {
  if(!state) { logState(state); throw "not a state"; }
  if(!state.process) {
    console.error(state.process);
    throw "not a process";
  }
  if(!state.process.type) {
    console.error(state.process);
    throw "no process type specified";
  }
  var type = state.process.type;
  var exc1 = ProcessInterpreter.customInterpretation;
  if(!(exc[type] && (exc[type] instanceof Function)) &&
     !(exc1[type] && exc1[type] instanceof Function)) {
    console.error(state.process);
    throw "unknown process type " + state.process.type;
  }
}

/**
 * reduces an object representing a string that depends on a state
 * (StatefulExpr, RefText etc)
 * to its canonical form, i.e. to a pure string or a RefText reference.
 * @param {undefined|StatefulExpr|RefText} val
 * @param {State} state
 * @return {RefText}
 * @private
 */
function toText(val, state) {
  if(val === undefined) return undefined;
  if(StatefulExpr.isStatefulExpr(val))
    val = StatefulExpr.evaluate(val, state);
  return RefText.makeRef(val, state);
}

/* StatefulExpr and Lambda in process */

// alias
/**
 * mapOnTermField(obj, f) iterates fields of obj and maps a value with f
 *   if it is a lambda term.
 * It returns a new object with the same set of keys and does not mutate obj.
 * @function
 * @param {Object} obj - an object
 * @param {function} f - a function
 * @return an object with the same set of keys
 */
//var mapOnTermField = Lambda.ExternalStandardImplementation.mapOnTermField;

/**
 * A wrapped version of mapOnTermField that preserves a prototype.
 * This function can't use private Lodash function `initCloneObject`,
 *   that is why `assign` + `clone` is used.
 * Also, it was called "mapSEField" to highlight its application.
 * @param {Process} process - a process (or any other object)
 * @param {function} func - a function of lambda term
 * @return a process (resp. an object of corresponding prototype)
 *   with the same set of keys and all the lambda term values being mapped
 */
// function mapSEFields(process, func) {
//   return _.assign(_.clone(process), mapOnTermField(process, func));
// }

/**
 * creates a function similar to mapSEFields,
 * but mapping only on specified set of keys, not every key.
 * @param {string[]} keys - an array of process's keys
 * whose values may be StatefulExpr
 * @return a function with the same signature as `mapSEFields`.
 *   It returns a process,
 *   whose StatefulExpr with given keys are mapped with a given function.
 */
function mapSEFields$(keys) {
  return function(process, func){
    var res = _.clone(process);
    keys.forEach(function(k){
      if(Lambda.isLambdaTerm(process[k]) || Lambda.isLambdaJSON(process[k]))
        res[k] = func(process[k]);
    });
    return res;
  };
}

/**
 * mapOnTermFieldDeep(obj, f) is a 'deep' analogue to mapOnTermField.
 * It traverses obj as a tree and applies f to each lambda term subobject.
 * When a lambda term is reached, traversing is no longer continued.
 * @param {Object} obj - an object
 * @param {function} f - a function of lambda term
 * @return an object with the same basic (i.e. except one of lambda
 *   term) structure
 */
function mapOnTermFieldDeep(obj, f) {
  var map = smartDeepMap(function(value) {
    return Lambda.isLambdaTerm(value) ? f(value) : value;
        //(Lambda.isLambdaJSON(value) ? f(value) : value);
  }, function(value) { return !Lambda.isLambdaTerm(value); });
  return map(obj);
};

var exc   = {},   // Interpretation: function(process, state)
    cln   = {},   // cloning: function(process, cloneProcess)
    toStr = {},   // toString: function(process, processToString)
    tp    = {},   // typing: Id
    lmap  = {},   // mapping on lambda subterms
    trmap = {};   // mapping: function(process, transformation)



/*    HTMLItem    */

function HTMLItem(expr, metadata) {
  if(_.keys(this).length > 0) return new HTMLItem(expr, metadata);
  this.type = "HTMLItem";
  this.expr = expr;
  this.metadata = metadata;
}

exc["HTMLItem"] = function(process, state) {
  var html = toText(process.expr, state);
  return {
    form: Forms.HTMLElementForm(html, process.metadata),
    reaction: emptyReaction("HTMLItem does not support reaction, \
its domain is empty.")
  };
};

cln["HTMLItem"] = function(process) {
  return new HTMLItem(process.expr, process.metadata);
};

toStr["HTMLItem"] = _.constant("[HTMLItem]");

tp["HTMLItem"] = FormatTypeId({
  expr: typeUnionId(RefTextId, Lambda.LambdaTermId)
}, "HTMLItem");

trmap["HTMLItem"] = function(p, t) { return p; };




/*    EmptyProcess    */

/* Code Explanation:
 *
 * EmptyProcess is not a parametric process like other process types.
 * Therefore there can be only one semantically distinguishable instance.
 * It is convenient to define a singleton `EmptyProcess` instance rather
 * than a function `EmptyProcess()` or a constructor `new EmptyProcess()`.
 * However, it would be consistent to make a constructor of EmptyProcess
 * and its type "EmptyProcess" to be namesakes, the instance and its
 * constructor has thus the same name. The constructor's name is local,
 * i.e. EmptyProcess refers to an instance as it was explained above,
 * but it figures in debug console.
 *
 * EmptyProcess is identified with a field isEmpty=true.
 * It seems to be crusial, especially when JSON representation is used.
 * However, it is possible to use reference comparison if JSON representation
 * is fully translated to 'inner' JS form before interpreting it.
 * Lambda system and parseProcess are halfway to introduces that approach.
 */
var EmptyProcess = new function EmptyProcess() {
  this.isEmpty = true;
  this.type = "EmptyProcess";;
};

exc["EmptyProcess"] = _.constant(undefined);

cln["EmptyProcess"] = function(process) {
  // without cloning, it is a singleton
  return EmptyProcess;
};

toStr["EmptyProcess"] = _.constant("[EmptyProcess]");

tp["EmptyProcess"] = typeIdByPredicate(function(p) { return p.isEmpty; },
  "not an EmptyProcess", "EmptyProcess");

trmap["EmptyProcess"] = function(p, t) { return p; };



function emptyReaction(msg) {
  return function() {
    throw msg || "Empty reaction was called. Perhaps, something went wrong.";
  };
}

var emptyFormReaction = {
  form: EmptyForm,
  reaction: emptyReaction("emptyFormReaction has an empty domain, \
its reaction must NOT be called.")
};

/* some stuff */

/**
 * deep variant of {@link module:RefText~makeRef makeRef}
 * `makeRefDeep(obj, state)` traverses `obj` and applies
 *   `RefText.makeRef` to each RefText object
 * @param {object} obj - an object to process
 * @param {State} state
 */
function makeRefDeep(obj, state) {
  var map = smartDeepMap(function(subobj){
    if(RefText.isText(subobj)) return RefText.makeRef(subobj, state);
    return subobj;
  }, function(s) { return !RefText.isText(s); });
  return map(obj);
}

/**
 * evaluate each StatefulExpr within a message object and add
 * `state.fullPath` to RefText prefix
 * @param msg - message object
 * @param {State} state - state object
 * @return a message object
 */
function evaluateMessage(obj, state) {
  var map = smartDeepMap(function(subobj){
    if(RefText.isText(subobj)) return RefText.makeRef(subobj, state);
    if(StatefulExpr.isStatefulExpr(subobj))
      return StatefulExpr.evaluate(subobj, state);
    return subobj;
  }, function(s) { return ! _.isString(s); });
  return map(obj);
}

/*    Message    */
function Message(msg, subj) {
  if(_.keys(this).length > 0) return new Message(msg, subj);
  this.type = "Message";
  this.msg  = msg;
  this.subj = subj;
}

exc["Message"] = function(process, state) {
  var fr = recFormReaction(state)(Pass);
  var msg = process.msg;
  if(StatefulExpr.isStatefulExpr(msg)) msg = StatefulExpr.evaluate(msg, state);
  msg = evaluateMessage(msg, state);
  if(process.subj) msg = _.fromPairs([[process.subj, msg]]);
  else if(msg.type == "adressed") msg = _.fromPairs([[msg.subj, msg.msg]]);
  else {
    console.log(process);
    throw "no subject given";
  }
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      /*
       * At that point msg must be of the form { subj: obj }
       * where obj is an arbitrary object that can be than processed
       * by Game transformers and/or UI components
       */
      r.msg = msg;
      return r;
    }
  };
};

cln["Message"] = function(process) {
  return new Message(process.msg);
};

toStr["Message"] = function(process) { return "[Message]"; };

tp["Message"] = FormatTypeId({
    msg: typeUnionId(MessageId, StatefulExprId)
  }, "Message Process");

trmap["Message"] = function(p, t) { return p; };

lmap["Message"] = mapSEFields$(["msg"]);





/*    Option    */

/**
 * Option(param) represents an atomic process of a single option
 *   for specified subject. It is used to construct complex processes
 *   with parallel (Par, Choice) or sequential (Seq) execution of Options.
 * A function Option is a constructor for JSON-like object
 *   with `type="Option"`, based on `param`.
 * Option process is attached to `param.subj` and produces
 *   OptionForm(param.caption) when `state.subj == param.subj`
 *   and EmptyForm otherwise.
 *
 * @summary an atomic process with one possible move option.
 *
 * @param param - an object of the following structure:
 * @param {string} param.subj - a subject option is attached to;
 * @param {RefText|StatefulExpr} param.caption - a caption of OptionForm;
 * @param [param.msg] - a message set to `state.msg` when Option is executed;
 * @param param.modifier - a transformation of `state.istate`;
 * @param {string|StatefulExpr} [param.nextSubj] -
 *   a subject who makes his move next;
 * @param {string} [param.label] - a label to wrap an Option process with
 * @return Option process
 */
function Option(param) {
  if(_.keys(this).length > 0) return new Option(param);
  this.type = "Option";
  this.subj = param.subj;
  if(param.caption)  this.caption  = param.caption;
  if(param.msg)      this.msg      = param.msg;
  if(param.modifier) this.modifier = param.modifier;
  if(param.nextSubj) this.nextSubj = param.nextSubj;
  if(param.label)    this.label    = param.label;
  if(param.mark)     this.mark     = param.mark;
}

exc["Option"] = function(process, state) {
  // sugar: option with label is PLabel+Option combination
  if(process.label)
    return recFormReaction(state)
      (new PLabel(process.label, _.omit(process, "label")));
  // sugar: option with mark is MarkForm+Option combination
  if(process.mark)
    return recFormReaction(state)
      (new MarkForm(process.mark, _.omit(process, "mark")));
  var p = _.clone(process); // process with evaluated StatefulExpressions
  /* caption */
  if(Lambda.isLambdaObject(p.caption))
    p.caption = Lambda.reduceLambdaObject(p.caption);
  p.caption = toText(p.caption, state);
  /* subj */
  if(StatefulExpr.isStatefulExpr(p.subj))
    p.subj = StatefulExpr.evaluate(p.subj, state);
  p.subj = StringId(p.subj);
  // sugar: option with msg is Seq of Option+Message combination
  if(process.msg)
    return recFormReaction(state)
      (new Seq([
        _.omit(process, "msg"),
        new Message(process.msg, process.subj)
      ]));
  /* nextSubj */
  if(StatefulExpr.isStatefulExpr(p.nextSubj))
    p.nextSubj = StatefulExpr.evaluate(p.nextSubj, state);
  /* msg */
  if(StatefulExpr.isStatefulExpr(p.msg))
    p.msg = StatefulExpr.evaluate(p.msg, state);
  // the main part
  if((_.isString(state.subj) && state.subj == p.subj) ||
     (_.isFunction(state.subj) && state.subj(p.subj)))
    return {
      form: InputOption(RefTextId(p.caption), state),
      reaction: function() {
        return _.assign(
          _.omit(_.clone(state), ["msg", "subj"]),
        {
          process: EmptyProcess,
          istate: p.modifier
            ? StatefulExpr.evaluate(p.modifier, state.istate)
            : state.istate,
          subj: p.nextSubj
        });
      }
    };
  else return emptyFormReaction;
};

cln["Option"] = function(process) {
  return new Option(process);
};

toStr["Option"] = function(process) {
  return [ "[", "Option caption=\"", this.caption, "\"]"].join("");
};

tp["Option"] = function Option_ProcessId(process) {
  try {
    ObjectId(process);
  } catch(e) {
    var msg = "Option process must be an object";
    throw new WrappedIdError(e, msg, process, Option_ProcessId);
  }
  try {
    if(process.subj !== undefined)
      typeUnionId(StringId, StatefulExprId)(process.subj);
  } catch(e) {
    var msg = "subj must be a string";
    throw new WrappedIdError(e, msg, process, Option_ProcessId);
  }
  try {
    if(process.nextSubj !== undefined) RefTextId(process.nextSubj);
  } catch(e) {
    var msg = "nextSubj must be a string";
    throw new WrappedIdError(e, msg, process, Option_ProcessId);
  }
  try {
    UnionId([RefTextId, StatefulExprId, Lambda.LambdaId])(process.caption);
  } catch(e) {
    var msg = "caption must be RefText, StatefulExpr or Lambda";
    throw new WrappedIdError(e, msg, process, Option_ProcessId);
  }
  return process;
};

trmap["Option"] = function(p, t) { return p; };

lmap["Option"] = function(p, f) {
  var basicMap = mapSEFields$(["subj", "nextSubj", "caption"]);
  var c = p.caption;
  if(Lambda.isLambdaObject(c))
    c = Lambda.wrapToObject(f(c.term));
  if(StatefulExpr.isStatefulExpr(c) || Lambda.isLambdaJSON(c)) {
    c = f(c);
  }
  return new Option(_.assign(basicMap(p, f), { caption: c }));
};




/*    PassOption    */

function PassOption(param) {
  if(_.keys(this).length > 0) return new PassOption(param);
  this.type = "Pass";
  param = param || {};
  if(param.msg) this.msg = MessageId(param.msg);
  if(param.modifier) this.modifier = param.modifier;
  if(param.nextSubj) this.nextSubj = param.nextSubj;
  if(param.label) this.label = param.label;
};

exc["Pass"] = function(process, state) {
  var option = new Option(_.assign(_.clone(process), { subj: "instantly" }));
  delete option.msg;
  option.caption = "";
  if(process.msg)
    return recFormReaction(state)(new Seq([option, new Message(process.msg)]));
  return exc["Option"](option, state);
}

cln["Pass"] = function(process) {
  return new PassOption(process);
};

toStr["Pass"] = _.constant("[Pass]");

tp["Pass"] = ObjectId; // TODO: define this type

trmap["Pass"] = function(p, t) { return p; };

lmap["Pass"] = function(p, f) {
  var option = lmap["Option"](p);
  option.type = "Pass";
  if(option.subj !== undefined && option.subj != "instantly") {
    console.warn("Pass has a subj=" + option.subj);
    delete option.subj;
  }
  return option;
};





/*    ShowText    */

function ShowText(param) {
  if(_.keys(this).length > 0) return new ShowText(param);
  var subj = param.subj;
  // sugar for common msg types
  if(param.html)      var msg = { type: "html", html: param.html };
  else if(param.text) var msg = { type: "text", text: param.text };
  else if(param.msg)  var msg = MessageId(param.msg);
  else throw "unknow ShowText msg format";
  // sugar for format msg
  if(param.rules) msg = RefText.FormatedText(msg, param.rules);
  msg = {
    type: "adressed",
    subj: subj,
    msg:  msg
  };
  
  this.type = "ShowText";
  this.subj = subj;
  this.msg  = MessageId(msg);
};

exc["ShowText"] = exc["Pass"];

cln["ShowText"] = function(process) {
  return new ShowText(process);
};

toStr["ShowText"] = function(process) {
  return "[ShowText for " + process.subj + "]";
};

tp["ShowText"] = FormatTypeId({ subj: StringId, msg: MessageId }, "ShowText");

trmap["ShowText"] = function(p, t) { return p; };

lmap["ShowText"] = mapOnTermFieldDeep;




/*    Seq    */

function Seq(subprocesses) {
  if(_.keys(this).length > 0) return new Seq(subprocesses);
  this.subprocesses = subprocesses;
  this.type = "Seq";
}

exc["Seq"] = function(process, state) {
  var procs = Lists.ListlikeToArray(process.subprocesses);
  // procs is supposed to be non-empty
  // however, let's check zero-length case
  if(procs.length == 0) return;
  if(procs.length == 1) {
    return recFormReaction(state)(procs[0]);
  } else {
    var fst = _.first(procs);
    var rst = _.tail (procs);
    var fr  = recFormReaction(state)(fst);
    if(!fr) return recFormReaction(state)(Seq(rst));
    
    if(!config.Processes.showSeqRest) {
      // everything but the first process ignored
      return {
        form: fr.form,
        reaction: function(v) {
          var r = fr.reaction(v);
          r.process = Seq(_.concat([r.process], rst));
          return r;
        }
      };
    } else {
      // The rest of the sequence is locked
      return {
        form: SumForm(_.concat(
          [fr.form],
          LockedForm(SumForm(_.chain(rst)
           .reject({ type: "Dep" }) // ignore Dep and Lambda processes
           .reject({ type: "Lambda" })
           .reject({ type: "OnlyWhen" })
           .reject({ type: "DoDangerousAction" })
           .map(function(proc) {
              var fr = recFormReaction(state)(proc);
              if(!fr) return undefined;
              return fr.form;
            })
            .filter() // _.filter removes undefined items
            .uniqWith(function(f1, f2) { return _.isEqual(f1, f2); })
            .value()))
        )),
        reaction: function(v) {
          // assert (v.mark == 0)
          var r = fr.reaction(v.value);
          r.process = Seq(_.concat([r.process], rst));
          return r;
        }
      };
    }
  }
};


cln["Seq"] = function(process) {
  return new Seq(process.subprocesses);
};

toStr["Seq"] = function(process) {
  return "Seq[" + process.subprocesses.join(", ") + "]";
};

tp["Seq"] = FormatTypeId({
    subprocesses: UnionId([
        TypedArrayId(ProcessId),
        Lambda.LambdaId,
        Lists.ListObjectId
      ])
  }, "Seq");

trmap["Seq"] = function(p, t) {
  if(Lambda.isLambdaObject(p.subprocesses)) return p;
  if(Lists.isListObject(p.subprocesses)) return p;
  return new Seq(UntypedArrayId(p.subprocesses).map(t));
};

lmap["Seq"] = function(p, f) {
  p = mapSEFields$(["subprocesses"])(p, f);
  if(Lambda.isLambdaObject(p.subprocesses))
    return new Seq(f(p.subprocesses));
  if(Lists.isListObject(p.subprocesses)) {
    var map = Lambda.ExternalObjectInterpretation[Lists.ListMarker].map;
    var listterm = p.subprocesses.list;
    var subprocs = { type: Lists.ListMarker, list: map(listterm, f) };
    return new Seq(subprocs);
  }
  return p;
};




/*    Par    */

function Par(subprocesses, caption, subj) {
  if(_.keys(this).length > 0) return new Par(subprocesses, caption, subj);
  if(_.isArray(subprocesses))
    subprocesses = _.flattenDeep(subprocesses);
  this.subprocesses = subprocesses;
  if(caption !== undefined) this.caption = caption;
  if(subj !== undefined) this.subj = subj;
  this.type = "Par";
}

exc["Par"] = function(process, state) {
  process = _.clone(process);
  process.subprocesses = Lists.ListlikeToArray(process.subprocesses);
  process.caption = Lambda.reduceLambdaObject(process.caption);
  // process.subprocesses supposed to be non-empty
  if(process.subprocesses.length == 0) return;
  if(process.subprocesses.length == 1 && process.caption === undefined)
    return recFormReaction(state)(process.subprocesses[0]);
  // form-reaction array
  var frs = process.subprocesses.map(recFormReaction(state));
  var forms = [];                     // well, imperative solution appeared
  var reactions = [];                 // to be simplier
  var finish = true;
  frs.forEach(function(fr, index) {
    if(fr) {
      if(process.subprocesses[index].type != "HTMLItem") finish = false;
      if(Forms.isEmpty(fr.form)) return;
      forms.push(fr.form);
      reactions.push(function(v) {
        var r = fr.reaction(v);
        //var subprocs = process.subprocesses.map(cloneProcess);
        var subprocs = _.clone(process.subprocesses);
        subprocs[index] = r.process;
        r.process = Par(subprocs, process.caption, process.subj);
        return r;
      });
    }
  });
  /* now forms and reactions are arrays of the same size
     they contain destructured (unpaired) non-undefined form-reactions. */
  if(finish) return undefined;
  
  // SumForm([]) ~~ EmptyForm
  if(forms.length == 0) return emptyFormReaction;
  var subj = process.subj;
  if(StatefulExpr.isStatefulExpr(subj))
    var subj = StatefulExpr.evaluate(subj, state);
  var combinedForm;
  if(subj !== undefined && subj != state.subj) {
    combinedForm = SumForm(forms);
  } else {
    var formCaption = toText(process.caption, state);
    combinedForm = SumForm(forms, formCaption);
  }
  // this line removes Par/Choice unfoldable option with the only caption inside
  // if(combinedForm.isEffectivelyEmpty()) return emptyFormReaction;
  if(! _.some(combinedForm.subforms, { type: "LockedForm" }) && Forms.isEffectivelyEmpty(combinedForm))
    return emptyFormReaction;
  // combinig
  return {
    form: combinedForm,
    reaction: function(v) { return reactions[v.mark](v.value); }
  };
};

toStr["Par"] = function(process) {
  var args = process.subprocesses.join(", ");
  var header = process.caption ? "Par<" + process.caption + ">" : "Par";
  return "[" + header + "(" + args +")]";
};

cln["Par"] = function(process) {
  return new Par(process.subprocesses, process.caption, process.subj);
};

tp["Par"] = FormatTypeId({
    subprocesses: UnionId([
        TypedArrayId(ProcessId),
        Lambda.LambdaId,
        Lists.ListObjectId
      ]),
    subj: OptionalId(UnionId([RefTextId, StatefulExprId, Lambda.LambdaId])),
    caption: OptionalId(RefTextId)
  }, "Par");

trmap["Par"] = function(p, t) {
  if(Lambda.isLambdaObject(p.subprocesses)) return p;
  if(Lists.isListObject(p.subprocesses)) return p;
  return new Par(UntypedArrayId(p.subprocesses).map(t), p.caption, p.subj);
};

lmap["Par"] = function(p, f) {
  p = mapSEFields$(["subprocesses", "caption"])(p, f);
  var subprocs = p.subprocesses;
  if(Lambda.isLambdaObject(p.subprocesses))
    subprocs = f(p.subprocesses);
  if(Lists.isListObject(p.subprocesses)) {
    var map = Lambda.ExternalObjectInterpretation[Lists.ListMarker].map;
    var listterm = p.subprocesses.list;
    subprocs = { type: Lists.ListMarker, list: map(listterm, f) };
  }
  return new Par(subprocs,
    Lambda.isLambdaObject(p.caption) ? f(p.caption) : p.caption,
    Lambda.isLambdaObject(p.subj)    ? f(p.subj)    : p.subj);
};




/*    Choice    */

function Choice(subprocesses, caption, subj) {
  if(_.keys(this).length > 0) return new Choice(subprocesses, caption, subj);
  if(_.isArray(subprocesses))
    subprocesses = _.flattenDeep(subprocesses);
  this.subprocesses = subprocesses;
  if(caption !== undefined) this.caption = caption;
  if(subj    !== undefined) this.subj    = subj;
  this.type = "Choice";
}

exc["Choice"] = function(process, state) {
  process = _.clone(process);
  process.subprocesses = Lists.ListlikeToArray(process.subprocesses);
  process.caption = Lambda.reduceLambdaObject(process.caption);
  // process.subprocesses supposed to be non-empty
  if(process.subprocesses.length == 0) return;
  if(process.subprocesses.length == 1 && process.caption === undefined)
    return recFormReaction(state)(process.subprocesses[0]);
  // form-reaction array
  var frs = process.subprocesses.map(recFormReaction(state));
  var forms = [];                     // well, imperative solution appeared
  var reactions = [];                 // to be simplier
  var finish = true;
  frs.forEach(function(fr, index) {
    if(fr) {
      if(process.subprocesses[index].type != "HTMLItem") finish = false;
      if(Forms.isEmpty(fr.form)) return;
      forms.push(fr.form);
      reactions.push(fr.reaction);
    }
  });

  /* now forms and reactions are arrays of the same size
     they contain destructured (unpaired) non-undefined form-reactions. */
  if(finish) return undefined;
  
  // SumForm([]) ~~ EmptyForm
  if(forms.length == 0) return emptyFormReaction;
  var combinedForm, subj = process.subj;
  if(StatefulExpr.isStatefulExpr(subj))
    var subj = StatefulExpr.evaluate(subj, state);
  if(subj !== undefined && subj != state.subj) {
    combinedForm = SumForm(forms);
  } else {
    var formCaption = toText(process.caption, state);
    combinedForm = SumForm(forms, formCaption);
  }
  // this line removes Par/Choice unfoldable option with the only caption inside
  if(Forms.isEffectivelyEmpty(combinedForm)) return emptyFormReaction;
  // combinig
  return {
    form: combinedForm,
    reaction: function(v) { return reactions[v.mark](v.value); }
  };
};

toStr["Choice"] = function(process) {
  var args = process.subprocesses.join(", ");
  var header = process.caption ? "Choice<" + process.caption + ">" : "Choice";
  return "[" + header + "(" + args +")]";
};

cln["Choice"] = function(process) {
  return new Choice(process.subprocesses, process.caption, process.subj);
};

tp["Choice"] = FormatTypeId({
    subprocesses: UnionId([
        TypedArrayId(ProcessId),
        Lambda.LambdaId,
        Lists.ListObjectId
      ]),
    subj: OptionalId(UnionId([RefTextId, StatefulExprId, Lambda.LambdaId])),
    caption: OptionalId(RefTextId)
  }, "Choice");

trmap["Choice"] = function(p, t) {
  if(Lambda.isLambdaObject(p.subprocesses)) return p;
  if(Lists.isListObject(p.subprocesses)) return p;
  return new Choice(UntypedArrayId(p.subprocesses).map(t), p.caption, p.subj);
};

lmap["Choice"] = function(p, f) {
  p = mapSEFields$(["subprocesses", "caption"])(p, f);
  var subprocs = p.subprocesses;
  if(Lambda.isLambdaObject(p.subprocesses))
    subprocs = f(p.subprocesses);
  if(Lists.isListObject(p.subprocesses)) {
    var map = Lambda.ExternalObjectInterpretation[Lists.ListMarker].map;
    var listterm = p.subprocesses.list;
    subprocs = { type: Lists.ListMarker, list: map(listterm, f) };
  }
  return new Choice(subprocs,
    Lambda.isLambdaObject(p.caption) ? f(p.caption) : p.caption,
    Lambda.isLambdaObject(p.subj)    ? f(p.subj)    : p.subj);
};





/*    Select    */

function Select(params) {
  if(_.keys(this).length > 0) return new Select(params);
  /*
  this.subj = params.subj;
  this.caption = params.caption;
  this.options = params.option;
  this.title = params.title;
  */
  this.params = params;
  this.type = "Select";
}

function transpileSelect(params) {
  var subj = params.subj;
  var title = params.title;
  var subtitle = params.subtitle;
  var caption = params.caption;
  var options = params.options;
  var mark = 'select-' + Math.floor(Math.random() * 1024 * 1024);
  var subprocesses = options.map(function(option) {
    var op = Option({ mark, subj, caption: option.caption });
    return (option.then === undefined) ? op : Seq([op, option.then]);
  });
  if(title !== undefined)
    subprocesses = [MarkForm(mark, Title(title))].concat(subprocesses);
  return CollectSumFormItems(mark, FlattenForm(Choice(subprocesses, caption)), caption);
};

exc["Select"] = function(process, state) {
  return recFormReaction(state)(transpileSelect(process.params));
};

toStr["Select"] = function(process) {
  return "[Select]";
};

cln["Select"] = function(process) {
  return new Select(process.params);
};

tp["Select"] = FormatTypeId({
    params: FormatTypeId({
      subj: StringId,
      caption: RefTextId,
      title: RefTextId,
      options: TypedArrayId(
        FormatTypeId({
          caption: RefTextId,
          then: ProcessId
        }))
    }, "Select params")
  }, "Select");

trmap["Select"] = function(p, t) {
  var params = _.clone(p.params);
  params.options = params.options.map(function(option) {
    return { caption: option.caption, then: t(option.then) };
  });
  return new Select(params);
};

lmap["Select"] = function(p, f) {
  return p;
};




/*   MarkForm    */
function MarkForm(mark, subprocess) {
  if(_.isArray(subprocess))
    return subprocess.map(function(p) { return new MarkForm(mark, p); });
  if(_.keys(this).length > 0) return new MarkForm(mark, subprocess);
  this.type = "MarkForm";
  this.mark = mark;
  this.subprocess = subprocess;
}

exc["MarkForm"] = function(process, state) {
  var fr = recFormReaction(state)(process.subprocess);
  if(fr === undefined) return undefined;
  return {
    form: {
			type: "MarkedForm",
      mark: process.mark,
      value: fr.form,
    },
    reaction: function(v) {
      var r = fr.reaction(v);
      r.process = new MarkForm(process.mark, r.process);
      return r;
    }
  };
};

toStr["MarkForm"] = function(p) { return p.subprocess; };

cln["MarkForm"] = function(p) { return new MarkForm(p.mark, p.subprocess); };

tp["MarkForm"] = FormatTypeId({
    mark: _.identity,
    subprocess: ProcessId
  }, "MarkForm");

trmap["MarkForm"] = function(p, t) {
  return new MarkForm(p.mark, t(p.subprocess));
};

lmap["MarkForm"] = function(p, f) { return p; };





/*   CollectSumFormItems    */
function CollectSumFormItems(mark, subprocess, caption, title) {
  if(_.keys(this).length > 0)
    return new CollectSumFormItems(mark, subprocess, caption, title);
  this.type = "CollectSumFormItems";
  this.mark = mark;
  this.subprocess = subprocess;
  this.caption = caption;
  this.title = title; // a process (e.g. HTMLItem)
}

exc["CollectSumFormItems"] = function(process, state) {
  if(process.title) {
    var tform = recFormReaction(state)(process.title).form;
    var p = AddZeroElements([tform],
      CollectSumFormItems(process.mark, process.subprocess, process.caption));
    var fr = recFormReaction(state)(p);
    if(fr === undefined) return undefined;
    return {
      form: fr.form,
      reaction: function(v) {
        return _.merge(fr.reaction(v), { process: { title: process.title } });
      }
    };
  }
  var fr = recFormReaction(state)(process.subprocess);
  if(fr === undefined) return undefined;
  if(fr.form.type != "SumForm")
    return {
      form: fr.form,
      reaction: function(v) {
        var r = fr.reaction(v);
        r.process = new CollectSumFormItems(process.mark, r.process,
            process.caption, process.title);
        return r;
      }
    };
  var
    subforms  = fr.form.subforms,
    subforms1 = subforms.filter(function(f){ return f.mark == process.mark; }),
    subforms2 = subforms.filter(function(f){ return f.mark != process.mark; }),
    caption   = process.caption && toText(process.caption, state),
    collectedSubforms = subforms1.map(function(f) { return f.value; }),
    form = new SumForm(
      [
        collectedSubforms.length > 0 ?
          new SumForm(collectedSubforms, caption) :
          EmptyForm
      ].concat(subforms2)
    ),
    reaction0 = fr.reaction;
  subforms1 = subforms2 = fr = undefined;
  return {
    form: form,
    reaction: function(v) {
      if(v.mark == 0) {
        var indices = subforms
          .map(function(f, index){
              if(f.mark == process.mark) return index;
            })
          .filter(function(x) { return x !== undefined; });
        var index = indices[v.value.mark];
        var r = reaction0({ mark: index, value: v.value.value });
      } else {
        var indices = subforms
          .map(function(f, index){
              if(f.mark != process.mark) return index;
            })
          .filter(function(x) { return x !== undefined; });
        var index = indices[v.mark - 1];
        var r = reaction0({ mark: index, value: v.value });
      }
      r.process =
          new CollectSumFormItems(process.mark, r.process,
            process.caption, process.title);
      return r;
    }
  };
};

toStr["CollectSumFormItems"] = function(p) { return p.subprocess; };

cln["CollectSumFormItems"] = function(p) {
  return new CollectSumFormItems(p.mark, p.subprocess, p.caption, p.title);
};

tp["CollectSumFormItems"] = FormatTypeId({
    mark: _.identity,
    subprocess: ProcessId
  }, "CollectSumFormItems");

trmap["CollectSumFormItems"] = function(p, t) {
  return new CollectSumFormItems(p.mark, t(p.subprocess), p.caption,
    p.title ? t(p.title) : undefined);
};

lmap["CollectSumFormItems"] = function(p, f) { return p; };





/*   FlattenForm    */
function FlattenForm(subprocess) {
  if(_.keys(this).length > 0) return new FlattenForm(subprocess);
  this.type = "FlattenForm";
  this.subprocess = subprocess;
}

exc["FlattenForm"] = function(process, state) {
  var fr = recFormReaction(state)(process.subprocess);
  if(!fr) return fr;
  var ft = flattenTrans(fr.form);
  return {
    form: ft.form,
    reaction: function(v) {
      var r = fr.reaction(ft.map(v));
      r.process = new FlattenForm(r.process);
      return r;
    }
  };
};

cln["FlattenForm"] = function(p) { return new FlattenForm(p.subprocess); };

toStr["FlattenForm"] = function(p) { return p.subprocess; };

tp["FlattenForm"] = FormatTypeId({ subprocess: ProcessId }, "FlattenForm");

trmap["FlattenForm"] = function(p, t) {
  return new FlattenForm(t(p.subprocess));
};

lmap["FlattenForm"] = function(p, f) { return p; };




/*   OnlyWhen    */
function OnlyWhen(condition, process) {
  if(_.keys(this).length > 0) return new OnlyWhen(condition, process);
  this.process = process;
  // this.condition = StatefulExprId(condition);
  this.condition = condition;
  this.type = "OnlyWhen";
}

exc["OnlyWhen"] = function(process, state) {
  var fr = recFormReaction(state)(process.process);
  if(!fr) return { form: EmptyForm, reaction: emptyReaction("OnlyWhen") };
  if(StatefulExpr.evaluate(process.condition, state)) return fr;
  else return {
    form: LockedForm(fr.form),
    reaction: emptyReaction("Locked form has an empty domain, reaction must NOT be called.")
  };
};

cln["OnlyWhen"] = function(process) {
  return new OnlyWhen(process.condition, process.process);
};

toStr["OnlyWhen"] = function(process) {
  return "[OnlyWhen " + process.process + "]";
};

tp["OnlyWhen"] = FormatTypeId({
  process: ProcessId,
  condition: typeUnionId(
    StatefulExprId, 
    function(x) { return tp["ProcessVar"](x); })
  }, "OnlyWhen");

trmap["OnlyWhen"] = function(p, t) {
  return new OnlyWhen(p.condition, t(p.process));
};

lmap["OnlyWhen"] = mapSEFields$(["condition", "process"]);




/**   ReadValue   */
function ReadValue(subj, form, varname) {
  if(_.keys(this).length > 0) return new ReadValue(subj, form, varname);
  this.type = "ReadValue";
  this.subj = subj;
  this.form = form;
  this.varname = varname;
}

exc["ReadValue"] = function(process, state) {
  if ((typeof state.subj !== 'string' || process.subj !== state.subj) && (typeof state.subj !== 'function' || !state.subj(process.subj)))
    return emptyFormReaction;
  return {
    form: process.form,
    reaction: function (v) {
      var s = _.clone(state);
      delete s.subj;
      s.istate = _.clone(s.istate);
      s.istate[process.varname] = v;
      s.process = EmptyProcess;
      return s;
    }
  };
};

cln["ReadValue"] = function(process) {
  return new ReadValue(process.subj, process.form, process.varname);
};

toStr["ReadValue"] = function(process) {
  return "[ReadValue " + process.varname + "]";
};

tp["ReadValue"] = FormatTypeId({ form: ObjectId, varname: StringId }, "ReadValue");

trmap["ReadValue"] = lmap["ReadValue"] = cln["ReadValue"];




/*    Cont    */

function Cont(subj, form, term) {
  if(_.keys(this).length > 0) return new Cont(subj, form, term);
  this.type = "Cont";
  this.subj = subj;
  this.form = form;
  this.term = term;
}

exc["Cont"] = function(process, state) {
  if (process.subj !== undefined && (typeof state.subj !== 'string' || process.subj !== state.subj) && (typeof state.subj !== 'function' || !state.subj(process.subj)))
    return emptyFormReaction;
  return {
    form: process.form,
    reaction: function (v) {
      var s = _.clone(state);
      delete s.subj;
      s.process = {
				type: "Lambda",
				term: Lambda.Constructors.Application(process.term, Lambda.Constructors.External(v, "Object"))
			};
      return s;
    }
  };
};

cln["Cont"] = function(process) {
  return new Cont(process.subj, process.form, process.term);
};

toStr["Cont"] = function(process) {
  return "[Continuation]";
};

tp["Cont"] = FormatTypeId({ form: ObjectId, varname: StringId, term: Lambda.LambdaTermId }, "Cont");

trmap["Cont"] = cln["Cont"];
lmap["Cont"] = function(process, f) {
	return new Cont(process.subj, process.form, f(process.term));
};



/*    Dep    */

function Dep(func) {
  if(_.keys(this).length > 0) return new Dep(func);
  this.type = "Dep";
  this.func = StatefulExprId(func);
}

exc["Dep"] = function(process, state) {
  const proc = ProcessId(StatefulExpr.evaluate(process.func, state));
  return recFormReaction(state)(proc);
};

toStr["Dep"] = function(process) {
  return "[Dep …]";
};

cln["Dep"] = function(process) {
  return new Dep(_.clone(process.func));
};

tp["Dep"] = FormatTypeId({ func: StatefulExprId }, "Dep");

trmap["Dep"] = function(p, t) { return p; };

lmap["Dep"] = function(p, f) { return f(p); } // mapSEFields$(["func"]);





/*    PLabel    */

function PLabel(label, process) {
  if(_.keys(this).length > 0) return new PLabel(label, process);
  this.type = "PLabel";
  this.label = StringId(label);
  this.process = process;
}

exc["PLabel"] = function(process, state) {
  if(state.labelStatus[process.label].aborted) {
    if(config.Processes.labelLog) {
      console.log("Process " + process.label + " was aborted");
    }
    return undefined;
  }
  if(state.labelStatus[process.label].stopped) {
    if(config.Processes.labelLog) {
      console.log("Process " + process.label + " is stopped");
    }
    return emptyFormReaction;
  }
  var fr = recFormReaction(state)(process.process);
  if(!fr) return undefined;
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      //TODO: exclude `simplifyProcess().isEmpty`
      if(r.curTime !== undefined &&
          simplifyProcess(r.process).isEmpty) {
        if(config.Processes.labelLog) {
          console.log("PLabel: process finished");
        }
        r.labelStatus[process.label] = _.clone(r.labelStatus[process.label]);
        r.labelStatus[process.label].timestamp = r.curTime;
        r.process = EmptyProcess;
      } else {
        if(config.Processes.labelLog) {
          console.log("PLabel: non-empty process (not finished)");
          console.log(cloneProcess(simplifyProcess(r.process)));
        }
        r.process = PLabel(process.label, r.process);
      }
      return r;
    }
  };
};

toStr["PLabel"] = function(process) {
  return process.label + ": " + process.process;
};

cln["PLabel"] = function(process) {
  return new PLabel(process.label, process.process);
};

tp["PLabel"] = FormatTypeId({ label: StringId, process: ProcessId }, "PLabel");

trmap["PLabel"] = function(p, t) { return new PLabel(p.label, t(p.process)); };

lmap["PLabel"] = function(p, f) { return p; };



/*    PLock    */

function PLock(label, process, status) {
  if(_.keys(this).length > 0) return new PLock(label, process, status);
  this.type    = "PLock";
  this.label   = StringId(label);
  this.process = process;
  this.status  = status || {};
}

/* mutates state adding closedStates and stoppedLabeledProcesses */
function checkPLockState(state, label, status) {
  if(!state.labelStatus) state.labelStatus = {};
  if(!state.labelStatus[label]) {
    state.labelStatus = _.clone(state.labelStatus);
    state.labelStatus[label] = status;
  } else {
    console.warn("Label " + label + " is already in use");
  }
}

exc["PLock"] = function(process, state) {
  var initState = state;
  var label = process.label;
  var status = process.status;
  state = _.clone(state);
  checkPLockState(state, label, status);
  var fr = recFormReaction(state)(process.process);
  if(!fr) return fr;
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      var newStatus = r.labelStatus[label];
      delete r.labelStatus[label];
      if(config.Processes.labelLog) {
        console.log("PLock " + label + " new status");
        console.log(newStatus);
      }
      r.process = PLock(label, r.process, newStatus);
      return r;
    }
  };
};

cln["PLock"] = function(process) {
  return new PLock(process.label, process.process);
};

toStr["PLock"] = function(process) {
  return "[к " + process.label + ". " + process.process + "]";
};

tp["PLock"] = FormatTypeId({ label: StringId, process: ProcessId }, "PLock");

trmap["PLock"] = function(p, t) { return new PLock(p.label, t(p.process)); };

lmap["PLock"] = function(p, f) { return p; };




/*    StopProcess    */

function StopProcess(label) {
  if(_.keys(this).length > 0) return new StopProcess(label);
  this.type = "StopProcess";
  this.label = StringId(label);
}

exc["StopProcess"] = function(process, state) {
  if(state.subj != "instantly") return emptyFormReaction;
  return {
    form: InputOption(""),
    reaction: function() {
      var r = _.omit(_.clone(state), ["msg", "subj"]);
      r.process = EmptyProcess;
      r.labelStatus[process.label] = _.clone(r.labelStatus[process.label]);
      r.labelStatus[process.label].stopped = true;
      if(config.Processes.reactionStateLog) {
        console.log("StopProcess reaction state");
        logState(r);
      }
      return r;
    }
  };
};

toStr["StopProcess"] = _.constant("[StopProcess]");

cln["StopProcess"] = function(process) {
  return new StopProcess(process.label);
};

tp["StopProcess"] = FormatTypeId({ label: StringId }, "StopProcess");

trmap["StopProcess"] = function(p, t) { return p; };

lmap["StopProcess"] = function(p, f) { return p; };



/*    ResumeProcess    */

function ResumeProcess(label) {
  if(_.keys(this).length > 0) return new ResumeProcess(label);
  this.type = "ResumeProcess";
  this.label = StringId(label);
}

exc["ResumeProcess"] = function(process, state) {
  if(state.subj != "instantly") return emptyFormReaction;
  return {
    form: InputOption(""),
    reaction: function() {
      var r = _.omit(_.clone(state), ["msg", "subj"]);
      r.process = EmptyProcess;
      r.labelStatus[process.label] = _.clone(r.labelStatus[process.label]);
      r.labelStatus[process.label].stopped = false;
      if(config.Processes.reactionStateLog) {
        console.log("ResumeProcess reaction state");
        logState(r);
      }
      return r;
    }
  };
};

toStr["ResumeProcess"] = _.constant("[ResumeProcess]");

cln["ResumeProcess"] = function(process) {
  return new ResumeProcess(process.label);
};

tp["ResumeProcess"] = FormatTypeId({ label: StringId }, "ResumeProcess");

trmap["ResumeProcess"] = function(p, t) { return p; };

lmap["ResumeProcess"] = function(p, f) { return p; };



/*    AbortProcess    */

function AbortProcess(label) {
  if(_.keys(this).length > 0) return new AbortProcess(label);
  this.type = "AbortProcess";
  this.label = StringId(label);
}

exc["AbortProcess"] = function(process, state) {
  if(state.subj != "instantly") return emptyFormReaction;
  return {
    form: InputOption(""),
    reaction: function() {
      var r = _.omit(_.clone(state), ["msg", "subj"]);
      r.process = EmptyProcess;
      r.labelStatus[process.label] = _.clone(r.labelStatus[process.label]);
      r.labelStatus[process.label].aborted = true;
      if(config.Processes.reactionStateLog) {
        console.log("AbortProcess reaction state");
        logState(r);
      }
      return r;
    }
  };
};

toStr["AbortProcess"] = _.constant("[AbortProcess]");

cln["AbortProcess"] = function(process) {
  return new AbortProcess(process.label);
};

tp["AbortProcess"] = FormatTypeId({ label: StringId }, "AbortProcess");

trmap["AbortProcess"] = function(p, t) { return p; };

lmap["AbortProcess"] = function(p, f) { return p; };




/*    ProcessVar    */

function ProcessVar(varName) {
  if(_.keys(this).length > 0) return new ProcessVar(varName);
  this.type = "ProcessVar";
  this.varName = StringId(varName);
}

exc["ProcessVar"] = function(process, state) {
  // if(state.impl[process.varName])
  //   return recFormReaction(state)(state.impl[process.varName]);
  throw "Process variable " + process.varName + " is undefined";
};

cln["ProcessVar"] = function(process) {
  return new ProcessVar(process.varName);
};

toStr["ProcessVar"] = function(process) {
  return ProcessVar.varName;
};

tp["ProcessVar"] = FormatTypeId({ varName: StringId }, "ProcessVar");

trmap["ProcessVar"] = function(p, t) { return p; };

lmap["ProcessVar"] = function(p, f) { return p; };




/*    ProcessReplace    */

function ProcessReplace(process, rules) {
  if(_.keys(this).length > 0) return new ProcessReplace(process, rules);
  this.type = "ProcessReplace";
  this.process = process;
  this.rules = ObjectId(rules);
}

exc["ProcessReplace"] = function(process, state) {
  var recGoDown = transformProcess({
    "ProcessVar": function(p) {
      return process.rules[p.varName] ? process.rules[p.varName] : p;
    }
  });
  return recFormReaction(state)(recGoDown(process.process));
};

cln["ProcessReplace"] = function(process) {
  return new ProcessReplace(process.process, _.clone(process.rules));
};

toStr["ProcessReplace"] = function(process) {
  return "[ProcessReplace " + process.process + "]";
};

tp["ProcessReplace"] = FormatTypeId({ process: ProcessId, rules: ObjectId }, "ProcessReplace");

trmap["ProcessReplace"] = function(p, t) {
  return new ProcessReplace(t(p.process), p.rules);
  //TODO: should we transform rules as well?
};

lmap["ProcessReplace"] = function(p, f) {
  //p = mapSEFields(p, f);
  //p.rules = 
  //  _.mapValues(p.rules, function(rp) { return mapSEFields(rp, f); });
  return p;
};




/*    ProcessImplement    */

function ProcessImplement(process, rules) {
  if(_.keys(this).length > 0) return new ProcessImplement(process, rules);
  this.type = "ProcessImplement";
  this.process = process;
  this.rules = rules;
}

exc["ProcessImplement"] = function(process, state) {
  var exc_bak = _.clone(exc);
  _.forEach(process.rules, function(rule, varName) {
    if(rule.type != "setValue")  // TODO: check whether this line is meaningful
      exc[varName] = function(proc, state) {
        function getIState() {
          // ist is an inner state with setValue applied
          var ist = state.istate;
          var ev = StatefulExpr.evaluate;
          _.forEach(proc, function(value, key) {
            if(value.type == "setValue")
              ist = ev(setValue(key, ev(value.value, state)), ist);
          });
          return ist;
        }
        // proc is a defining process, e.g. If(c,t,f)
        // proc.type is a key of process.rules
        // p is process.rules[proc.type] (or just 'rule') with process vars replaced
        // st is a state with 'setValue' actions executed
        var p = ProcessReplace(rule, proc);
        var st = _.clone(state);
        st.istate = getIState();
        return recFormReaction(st)(p);
      };
  });
  var fr = recFormReaction(state)(process.process);
  exc = exc_bak;
  if(!fr) return fr;
  // just wrap process with ProcessImplement
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      r = _.clone(r);
      r.process = ProcessImplement(r.process, process.rules);
      return r;
    }
  };
};

cln["ProcessImplement"] = function(process) {
  return new ProcessImplement(process.process, process.rules);
};

toStr["ProcessImplement"] = function(process) {
  return "[ProcessImplement " + process.process + "]";
};

tp["ProcessImplement"] = FormatTypeId({ process: ObjectId, rules: ObjectId }, "ProcessImplement");

trmap["ProcessImplement"] = function(p, t) {
  return new ProcessImplement(t(p.process), p.rules);
  //TODO: should we transform rules as well?
};

lmap["ProcessImplement"] = function(p, f) {
  //p = mapSEFields(p, f);
  //p.rules = _.mapValues(p.rules, function(rp) {
  //  if(rp.type == "setValue")
  //    return { type: "setValue", value: f(rp.type) };
  //  return mapSEFields(rp, f);
  //});
  return p;
};




/*    UseText    */

function UseText(path, process) {
  if(_.keys(this).length > 0) return new UseText(path, process);
  this.type = "UseText";
  this.path = path;
  this.process = process;
}

exc["UseText"] = function(process, state) {
  var st = _.clone(state);
  st.process = process.process;
  var textPath1 = st.textPath;
  if(_.isArray(process.path))
    st.textPath = process.path;
  else
    st.textPath = StatefulExpr.evaluate(process.path, state);
  var fr = getFormReaction(st);
  if(!fr) return fr;
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      r.process = UseText(process.path, r.process);
      r.textPath = textPath1;
      return r;
    }
  };
};

cln["UseText"] = function(process) {
  return new UseText(process.path, process.process);
};

toStr["UseText"] = _.constant("[UseText]");

tp["UseText"] = FormatTypeId({
  path: typeUnionId(TypedArrayId(StringId), StatefulExprId, "Path"),
  process: ProcessId
}, "UseText");

trmap["UseText"] = function(p, t) {
  return new UseText(p.path, t(p.process));
};




/*    TextCd    */

function TextCd(relPath, process) {
  if(_.keys(this).length > 0) return new TextCd(relPath, process);
  this.type = "TextCd";
  this.relPath = relPath;
  this.process = process;
}

exc["TextCd"] = function(process, state) {
  var st = _.clone(state);
  st.process = process.process;
  var textPath1 = st.textPath;
  var relPath = process.relPath;
  if(! _.isArray(relPath))
    relPath = StatefulExpr.evaluate(process.relPath, state);
  st.textPath = st.textPath ? _.concat(st.textPath, relPath) : _.clone(relPath);
  if(config.Processes.RefTextLog) {
    console.log("text path: " + st.textPath.join("/"));
  }
  var fr = getFormReaction(st);
  if(!fr) return fr;
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      r.process = TextCd(process.relPath, r.process);
      r.textPath = textPath1;
      return r;
    }
  };
};

cln["TextCd"] = function(process) {
  return new TextCd(process.relPath, process.process);
};

toStr["TextCd"] = _.constant("[TextCd]");

tp["TextCd"] = FormatTypeId({
  relPath: typeUnionId(TypedArrayId(StringId), StatefulExprId, "Path"),
  process: ProcessId
}, "TextCd");

trmap["TextCd"] = function(p, t) {
  return new TextCd(p.relPath, t(p.process));
};



/*    SetSubjectState    */

function SetSubjectState(subj, diff) {
  if(window === this) return new SetSubjectState(subj, diff);
  this.type = "SetSubjectState";
  this.subj = subj;
  this.diff = diff;
}

exc["SetSubjectState"] = function(process, state) {
  var fr = recFormReaction(state)(Pass);
  var subj = toText(process.subj, state);
  var diff = StatefulExpr.evaluate(process.diff, state);
  if(!fr) return fr;
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      r.subjState = r.subjState ? _.clone(r.subjState) : {};
      r.subjState[subj] = _.clone(r.subjState[process.subj]);
      r.subjState[subj] = _.assign(r.subjState[subj], diff);
      return r;
    }
  };
}


cln["SetSubjectState"] = function(process) {
  return new SetSubjectState(process.subj, process.diff);
};

toStr["SetSubjectState"] = function(process) {
  return "[SetSubjectState " + process.subj + "]";
};

tp["SetSubjectState"] = FormatTypeId({
    subj: typeUnionId(StringId, StatefulExprId),
    diff: StatefulExprId
  }, "SetSubjectState");

trmap["SetSubjectState"] = function(p, t) { return p; };

lmap["SetSubjectState"] = mapSEFields$(["subj", "diff"]);




/*   DoDangerousAction    */

function DoDangerousAction(func) {
  if(_.keys(this).length > 0) return new DoDangerousAction(func);
  this.type = "DoDangerousAction";
  this.func = func;
}

exc["DoDangerousAction"] =
  function(process, state) {
    var f = process.func;
    var fr = recFormReaction(state)(Pass);
    if(!fr) return fr;
    return {
      form: fr.form,
      reaction: function(v) {
        if(_.isFunction(f)) f(state);
        else {
          f = toText(f, state);
          f = RefText.resolve(f);
          if(_.isString(f)) f = eval('(' + f + ')');
          if(_.isFunction(f)) f(state);
        }
        state.process = Pass;
        return state;
      }
    };
  };

cln["DoDangerousAction"] =
  function(process) { return process; };

toStr["DoDangerousAction"] =
  function(process) { return "[Do: " + process.func + "]"; };

tp["DoDangerousAction"] =
  FormatTypeId({
    func: UnionId([
      StringId,
      FunctionId,
      RefText.RefTextId,
      StatefulExpr.StatefulExprId
    ]) },
    "DoDangerousAction");

trmap["DoDangerousAction"] =
  function(p, t) { return p; };

lmap["DoDangerousAction"] =
  mapSEFields$(["func"]);




/*    PObject    */

/**
 * PObject(objName, objValue, process)
 * PObject(objName, process) === PObject(objName, undefined, process)
 */
function PObject(objName, objValue, process) {
  if(_.keys(this).length > 0) return new PObject(objName, objValue, process);
  // if(process === undefined) { process = objValue; objValue = undefined; }
  this.type     = "PObject";
  this.objName  = StringId(objName);
  this.objValue = objValue;
  this.process  = process; // ProcessId(process);
}

exc["PObject"] = function(process, state) {
  var st = _.clone(state);
  if(!st.objects) st.objects = {};
  st.objects[process.objName] = process.objValue;
  var fr = recFormReaction(st)(process.process);
  if(!fr) return fr;
  return {
    form: fr.form,
    reaction: function(v) {
      var r = fr.reaction(v);
      r.process = new PObject(process.objName, process.objValue, r.process);
      return r;
    }
  };
};

cln["PObject"] = function(process) {
  return new PObject(process.objName, process.objValue, process.process);
};

toStr["PObject"] = function(process) {
  return [ "<", process.objName, "=", process.objValue, ">",
           " | ", process.process, "]" ].join("");
};

tp["PObject"] = FormatTypeId({
    objName: StringId,
    objValue: _.identity,
    process: ProcessId
  }, "PObject");

trmap["PObject"] = function(p, t) {
  return new PObject(p.objName, p.objValue, t(p.process));
};

lmap["PObject"] = function(p, f) { return p; };




/*    ChangeForm    */

/**
 * Change form
 */
function ChangeForm(process, mForm, mVariant, subj) {
  if(_.keys(this).length > 0)
    return new ChangeForm(process, mForm, mVariant, subj);
  this.type     = "ChangeForm";
  this.process  = process;
  this.mForm    = mForm;
  this.mVariant = mVariant;
  this.subj     = subj;
}

exc["ChangeForm"] = function(process, state) {
  var
    subprocess = process.process,
    mForm      = process.mForm,
    mVariant   = process.mVariant,
    fr = recFormReaction(state)(subprocess);
  // if fr is undefined, return it
  if(fr === undefined) return fr;
  // when ChangeForm is devoted to another subj,
  // return fr with its reaction wrapped
  if(process.subj != undefined &&
    (_.isString(state.subj) ?
      process.subj != state.subj :
      !state.subj(process.subj))) {
    return {
      form: fr.form,
      reaction: function(v) {
        var r = fr.reaction(v);
        r.process = new ChangeForm(r.process, mForm, mVariant, process.subj);
        return r;
      }
    };
  }
  // a function to retreave a value of e
  function evalValue(e) {
    return _.isString(e) ? eval("(" + e + ")")(fr.form) :
      StatefulExpr.evaluate(e, fr.form);
  }
  // actual form and mapVariant functino
  var
    form       = evalValue(mForm),
    mapVariant = evalValue(mVariant);
  // wrap with eval if mapVariant is a string
  if(_.isString(mapVariant)) mapVariant = eval("(" + mapVariant + ")");
  return {
    form: form,
    reaction: function(v) {
      var r = fr.reaction(mapVariant(v));
      r.process = new ChangeForm(r.process, mForm, mVariant, process.subj);
      return r;
    }
  };
};

cln["ChangeForm"] = function(process) {
  return new ChangeForm(process.process, process.mForm,
    process.mVariant, process.subj);
};

toStr["ChangeForm"] = function(process) {
  return process.process;
};

tp["ChangeForm"] = FormatTypeId({
    process: ProcessId
  }, "ChangeForm");

trmap["ChangeForm"] = function(p, t) {
  return new ChangeForm(t(p.process), p.mForm, p.mVariant, p.subj);
};

lmap["ChangeForm"] = function(p, f) { return p; };








/*****************************************************************/
/*                            LAMBDA                             */
/*****************************************************************/

// marker of Process object in Lambda-term
var objType = "Process";

/**
 * ExternalObjectInterpretation.Process.map implementation
 * An argument and a result must be processes
 * @param obj - a process
 * @param f - lambda term transformation
 * @return a process
 * @private
 */
function EOImap(obj, f) {
  return processLambdaMap(f)(ProcessId(obj));
}

Lambda.ExternalObjectInterpretation[objType] = {
  map: EOImap,
  traverse: undefined, // use default implementation based on EOImap
  importFromJSON: parseProcess, // defined below
  exportToJSON: exportToJSON, // defined below
  id: ProcessId
};



exc["Lambda"] = function(process, state) {
  var p = Lambda.reduceLambdaObject(process);
  if(p.type == "Lambda") {
    console.error(p);
    throw "couldn't reduce lambda term to process";
  }
  return recFormReaction(state)(p);
};

tp["Lambda"] = Lambda.LambdaId;

trmap["Lambda"] = function(p, t) {
  return p;
};

cln["Lambda"] = _.identity;

toStr["Lambda"] = _.constant("<lambda>");

lmap["Lambda"] = function(p, f) {
  return {
    type: "Lambda",
    term: f(p.term)
  };
};

function reduceLambdaTermInTopLevel(process) {
  if(!lmap[process.type]) return process;
  console.log(process);
  var p = lmap[process.type](process, function(term) {
      var rterm = Lambda.reduce(term);
      console.log((rterm.list));
      if(Lambda.isListObject(rterm)) return Lists.ListToArray(rterm);
      return rterm;
    });
  console.log(p);
  return p;
}





/******************************************************************/
/*                     PROTOTYPES & TEMPLATES                     */
/******************************************************************/


var OptionPrototype = {
  
  changeState: function(mod) {
    var option = cln[this.type](this);
    option.modifier =
      this.modifier ? StatefulExpr.Map(mod, this.modifier) : mod;
    return option;
  },
  
  changeNotification: function(f) {
    var option = cln[this.type](this);
    option.msg = f(this.msg);
    return option;
  },
  
  rewriteNotification: function(msg) {
    var option = cln[this.type](this);
    option.msg = MessageId(msg);
    return option;
  },
  
  addNotification: function(msg) {
    msg = MessageId(msg);
    var option = cln[this.type](this);
    if(option.msg)
      option.msg = { type: "composed", parts: [option.msg, msg] }
    else
      option.msg = msg;
    option.msg = MessageId(option.msg);
    return option;
  }
  
};

_.assign(Option.prototype, OptionPrototype);
_.assign(PassOption.prototype, OptionPrototype);
_.assign(ShowText.prototype, OptionPrototype);

var Pass = new PassOption;

function Title(title, subtitle) {
  if(subtitle === undefined)
    return HTMLItem(title, "title");
  else
    /* this block converts title and subtitle to StatefulExpr */
    if(_.isString(title)) title = StatefulExpr.Pure(title);
    //else if(title.type == "RefText") title = RefTextValue(title);
    if(_.isString(subtitle)) subtitle = StatefulExpr.Pure(subtitle);
    //else if(subtitle.type == "RefText") subtitle = RefTextValue(subtitle);
    /* end of converting */
    return [
        HTMLItem(title, "title"),
        HTMLItem(title, "subtitle")
    ];
}

function AddZeroElements(elements, process, subj) {
  UntypedArrayId(elements);
  function mFormF(form) {
    if(form.type == "EmptyForm") return form;
    if(form.type !== "SumForm") {
      console.warn("SumForm expected");
      console.warn(form);
      return form;
    }
    var sf = _.clone(form);
    sf.subforms = elements.concat(form.subforms);
    return sf;
  }
  function mVariantF(form) {
    return function(v) {
      if(v.mark < elements.length)
        console.error("Zero elements do not have variants!");
      return {
        value: v.value,
        mark: v.mark - elements.length
      };
    };
  }
  var mForm = MultivariateFunction(mFormF, 1, ["Object"], "Object");
  var mVariant = MultivariateFunction(mVariantF, 1, ["Object"], "Object");
  return new ChangeForm(process, mForm, mVariant, subj);
}

// deprecated
function If(condition, trueproc, falseproc) {
  return Choice([
    OnlyWhen(condition, trueproc),
    OnlyWhen(StatefulExpr.condition.negate(condition), falseproc)
  ]);
}

// deprecated
function IfInstantly(condition, trueproc, falseproc) {
  return If(condition, Seq([Pass, trueproc]), Seq([Pass, falseproc]));
}

function Repeat(exitCondition, body) {
  return {
    type: "Repeat",
    exitCondition: exitCondition,
    body: body
  };
}
trmap["Repeat"] = function(p, t) {
  return new Repeat(p.exitCondition, t(p.body));
};
tp["Repeat"] = ObjectId; //TODO: write actual type
exc["Repeat"] = function(process, state) {
  var red = Seq([
    process.body,
    If(process.exitCondition,
      Pass,
      Seq([ Pass, Dep(StatefulExpr.Pure(process)) ])
    )
  ]);
  return recFormReaction(state)(red);
};

function Cond(condition, process) {
  return IfInstantly(condition, process, Pass);
}

function setHint(hint) {
  return ShowText({
    subj: "Hint",
    text: hint
  });
}





/*   assembled function    */

function getFormReaction(state) {
  // checking for errors
  if(config.Processes.checkingStateAndProcess) checkStateAndProcess(state);
  // everything is OK: redirecting…
  var type = state.process.type;
  var excToCall = exc[type] || ProcessInterpreter.customInterpretation[type];
  return excToCall(state.process, state);
}


/* curried modification of getFormReaction with process assignment */
function recFormReaction(state) {
  // process required, astate optional
  return function(process, astate) {
    ProcessId(process);
    return getFormReaction(_.assign(
      _.clone(state),        // actual state
      { process: process },  // replacing process
      astate || {}           // do other replacing if any
    ));
  };
}

/**
 * a function that returns an actual `tp` object (id for each process type).
 * @return a union of internal `tp` and expernal `Process.tp`.
 * @private
 */
function get_tp() {
  return _.assign(_.clone(tp), ProcessInterpreter.tp);
}

/**
 * a function that returns an actual `trmap` object.
 * @return a union of internal `tmap` and external one.
 * @private
 */
function get_tmap() {
  return _.assign(_.clone(trmap), ProcessInterpreter.trmap);
}

/**
 * a function that returns an actual `lmap` object.
 * @return a union of internal `lmap` and external one.
 * @private
 */
function get_lmap() {
  return _.assign(_.clone(lmap), ProcessInterpreter.lmap);
}

/**
 * converts a process to a string.
 * Non-process subobjects `obj` are represented with `obj.toString()`
 * (if needed).
 * This function can be used as a method of a process, so that
 * if no argument is given, `this` is used instead.
 * @param {Process} p
 * @return {string}
 */
function processToString(process) {
  // this line binds 'process' and 'this'
  // so that processToString function can be used as a method of a process
  if(process === undefined && this !== window) process = this;
  // core function
  // Note that this function cannot be declared outside, because
  // `toStr` object may be mutated and hence its value should be retrieved
  // on each `processToString` call
  var process2String = transformProcess(toStr,
      function(p) { return p.toString(); });
  return process2String(process);
}

function cloneProcess(process) {
  // this line binds 'process' and 'this'
  // so that processToString function can be used as a method of a process
  if(process === undefined && this !== window) process = this;
  return transformProcess(cln)(process);
}

/**
 * transformProcess(tr) is an endomorphism (i.e. transformation) on Process,
 * that applies a single-level transformation function to each level.
 * A single-level transformation is defined by 'tr' object:
 * if 'type' property of a process is a key of 'tr', tr[type] is used;
 * otherwise a process is returned without any change.
 * @param {Object} tr - collection of transformations
 * @param {function} [deflt] - default transformation
 */
function transformProcess(tr, deflt) {
  var utrmap = _.assign(_.clone(trmap), ProcessInterpreter.trmap);
  return function transformation(process) {
    var p = process;
    if(!p) throw "Process expected";
    if(utrmap[StringId(p.type)]) p = utrmap[p.type](p, transformation);
    else {
      console.error(p);
      throw "unknown trmap type " + p.type;
    }
    if(tr[p.type]) p = tr[p.type](p);
    else if(deflt !== undefined) p = deflt(p);
    return p;
  };
}

/**
 * processLambdaMap(f) returns a function that transforms a process
 * applying given function f to each lambda term within a process.
 * This function uses trmap to go down the process structure
 * and lmap to apply f to each subterm.
 * @param {function} f - a function of lambda term
 * @return transformation function of process
 */
function processLambdaMap(f) {
  // union of internal and external trmaps
  var utrmap = _.assign(_.clone(trmap), ProcessInterpreter.trmap);
  // union of internal and external lmap
  var ulmap = _.assign(_.clone(lmap), ProcessInterpreter.lmap);
  return function transformation(process) {
    var p = process;
    // run down process structure
    if(utrmap[p.type]) p = utrmap[p.type](p, transformation);
    // map function on p
    if(ulmap[p.type]) p = ulmap[p.type](p, f);
    return p;
  };
}

/**
 * parse JSON.
 * Process format is developed to use JSON objects 'as is',
 * However, Lambda uses two formats, external and internal, and one needs to
 *   converts all subterms to inner format.
 * Essentially `parseProcess` invokes `importFromJSON` for each lambda subterm
 *   and other convertors if any needed.
 * @param {JSON} obj
 * @return {Process} a process the JSON object represents
 */
function parseProcess(obj, env, lambdaImport) {
  function convert(t) {
    return lambdaImport(t, env);
  }
  var lambdaConverter = processLambdaMap(convert);
  return ProcessId(lambdaConverter(obj));
}

function exportToJSON(obj, prefered, forbidden, lambdaExport) {
  function convert(t) { return lambdaExport(t, prefered, forbidden); }
  var lambdaConverter = processLambdaMap(convert);
  return lambdaConverter(ProcessId(obj));
}







/**
 * @param {string} varName - a string
 * @param {*} varValue - any value
 * @return {StatefulExpr} StatefulExpr of state.istate
 */
function setValue(varName, varValue) {
  var fn = [
    "function(val, s) { var ms = _.clone(s);",
    "ms[\"", StringId(varName), "\"] = val;",
    "return ms; }" ].join("");
  return new C.Application(
    MultivariateFunction(fn, 2, ["Object", "Object"], "Object"),
    C.External(varValue, "Object"));
}

/**
 * @param {string} varName - a string
 * @param {*} varValue - any value
 * @return StatefulExpr of boolean type
 */
function isValue(varName, varValue) {
  var fn = "function(val, s) { return s.istate[\"" + varName + "\"] == val; }";
  return new C.Application(
    MultivariateFunction(fn, 2, ["Object", "Object"], "Boolean"),
    C.External(varValue, "Object"));
}

/**
 * @param {string} varName - a string
 * @param {Type} type - a lambda type of a result (optional, Object by default)
 * @return {StatefulExpr}
 */
function getValue(varName, type) {
  function fn(s) {
    if(s.istate["varName"] === undefined)
      console.warn("varName value undefined", s);
    return s.istate["varName"];
  }
  var fn = fn.toString().replace(/varName/g, varName);
  // if Boolean extracted, cast a value to a primitive Boolean
  // even if a Boolean object is in the state
  if(Types.equiv(type, "Boolean")) {
    fn = fn.replace("return ", "return true == ");
  }
  if(type === undefined)
    console.warn("getValue result's type is not defined");
  //TODO: rewrite using Object.get function
  return new C.External(fn, "Function", FnType("Object", type || "Object"));
}


/**
 * Possible arguments:
 * 1) objName, suffix, initValue, process
 * 2) objName, initValue, process
 * 3) objName, process
 * @param {string} objName
 * @param {string[]} [suffix=[]]
 * @param {*} [initValue=undefined]
 * @param {Process} process
 */
function useObjectValue(_1, _2, _3, _4) {
  var objName = _1, suffix, initValue, process;
  if(_3 === undefined && _4 === undefined) {
    suffix = [];
    initValue = undefined;
    process = _2;
  } else if(_4 === undefined) {
    suffix = [];
    initValue = _2;
    process = _3;
  }
  if(initValue === undefined)
    throw "undefined initValue";
  return {
    type: "useObjectValue",
    objName: StringId(objName),
    suffix: TypedArrayId(StringId)(suffix),
    initValue: initValue,
    process: process // ProcessId(process)
  }
}

exc["useObjectValue"] =
  (function(process, state) {
    // top-layer clone of state
    state = _.clone(state);
    // synonyms
    var
      objName = process.objName,
      suffix  = process.suffix,
      objectPull = state.objects;
    // add object storage to a state
    state.objects = _.clone(state.objects || {});
    // notify if initValue is undefined. That case might be a sign of an error
    if(process.initValue === undefined)
      console.error("empty initValue");
    // assign initValue to [objName, ...suffix]
    _.set(state.objects, _.concat([objName], suffix), process.initValue);
    // get form-reaction for subprocess for a state with a variable defined
    state.process = process.process;
    var fr = ProcessInterpreter.getFormReaction(state);
    if(!fr) return fr;
    return {
      form: fr.form,
      reaction: function(v) {
        var r = fr.reaction(v);
        // new process is useObjectValue with process of r
        var newProcess = _.clone(process);
        newProcess.process = r.process;
        newProcess.initValue = _.get(r.objects, _.concat([objName], suffix));
        // that case might be a sign of an error
        if(newProcess.initValue === undefined)
          console.error("undefined initValue: ", r.objects);
        // restoring objectPull
        r.objects = objectPull;
        // recording new useObjectValue process
        r.process = newProcess;
        return r;
      }
    };
  });

trmap["useObjectValue"] = function(p, t) {
  return {
    type: "useObjectValue",
    objName: p.objName,
    suffix: _.clone(p.suffix),
    initValue: p.initValue,
    process: t(p.process)
  };
};

lmap["useObjectValue"] = function(p, f) {
  var leoi = Lambda.ExternalObjectInterpretation;
  return {
    type: "useObjectValue",
    objName: p.objName,
    suffix: _.clone(p.suffix),
    initValue: Lambda.isLambdaTerm(p.initValue) ? f(p.initValue) : p.initValue,
    process: p.process
  };
};

tp["useObjectValue"] = FormatTypeId({
    objName: StringId,
    suffix: TypedArrayId(StringId),
    initValue: _.identity,
    process: ProcessId
  }, "useObjectValue");

function getObjectValue(objName, suffix, type, defaultValue) {
  if (type === undefined) {
    console.log('getObjectValue: Type of ' + objName + ' variable is undefined');
  }
  var
    fn = getObjectValueFunction.toString(),
    arity = 4,
    resType  = type || Types.AnyType,
    argTypes = ["String", "Object", resType, "Object"];
  return C.Application(C.Application(C.Application(
    MultivariateFunction(fn, arity, argTypes, resType),
    C.External(objName, "String")),
    C.External(suffix || [], "Object")),
    C.External(defaultValue || [], resType, resType));
}

function getObjectValueFunction(objName, suffix, defaultValue, state) {
  // if there is no object storage, return undefined
  if(!state.objects) {
    console.error('State.objects is undefined!');
    return;
  }
  // extract an object by its name
  var obj = state.objects[objName];
  console.log('getObjectValue = ', obj);
  console.log(suffix);
  if (obj === undefined) return defaultValue;
  // if no suffix given, return the object
  if(!suffix || !suffix.length) return obj;
  // return a part of the object
  var r = _.get(obj, suffix);
  if(r === undefined) return defaultValue;
  if(r instanceof Boolean) return r.valueOf();
  return r;
};

function isObjectValueDefined(objName, suffix) {
  var
    fn = isObjectValueDefinedFunction.toString(),
    arity = 3,
    argTypes = ["String", "Object", "Object"],
    resType  = "Boolean";
  return C.Application(
    C.Application(
      MultivariateFunction(fn, arity, argTypes, resType),
      C.External(objName, "String")),
    C.External(suffix || [], "Object")
    );
}

function isObjectValueDefinedFunction(objName, suffix, state) {
  // if there is no object storage, return undefined
  if(!state.objects) return false;
  // extract an object by its name
  var obj = state.objects[objName];
  // if no suffix given, return the object
  if(!suffix || !suffix.length) return obj !== undefined;
  // return a part of the object
  var r = _.get(obj, suffix);
  return r !== undefined;
};



function setObjectValue(objName, objValue, suffix) {
  if(_.isString(objValue) || _.isNumber(objValue) || _.isNull(objValue) ||
     _.isUndefined(objValue)) objValue = StatefulExpr.Pure(objValue);
  if(suffix !== undefined) UntypedArrayId(suffix);
  if(objValue === undefined) console.warn("objValue undefined");
  return {
    type: "setObjectValue",
    objName: objName,
    objValue: objValue,
    suffix: suffix
  };
}

exc["setObjectValue"] =
  (function(process, state) {
    // form-reaction as if a process was Pass
    var s = _.assign(_.clone(state), { process: Pass });
    var fr = ProcessInterpreter.getFormReaction(s);
    if(!fr) return fr;
    return {
      form: fr.form,
      reaction: function(v) {
        var r = fr.reaction(v);
        // storage for local objects
        r.objects = r.objects ? _.clone(r.objects) : {};
        // synonyms
        var o     = r.objects,
            name  = process.objName,
            value = StatefulExpr.isStatefulExpr(process.objValue) ? StatefulExpr.evaluate(process.objValue, state) : process.objValue,
            path  = _.concat([process.objName], process.suffix || []);
        // local object in storage to modify
        o[name] = o[name] ? _.clone(o[name]) : {};
        _.set(o, path, value);
        return r;
      }
    };
  });

trmap["setObjectValue"] = function(p, t) {
  return p;
};

lmap["setObjectValue"] = function(p, f) {
  var leoi = Lambda.ExternalObjectInterpretation;
  return {
    type: "setObjectValue",
    objName: p.objName,
    suffix: _.clone(p.suffix),
    objValue: Lambda.isLambdaTerm(p.objValue) ? f(p.objValue) : p.objValue
  };
};

tp["setObjectValue"] = FormatTypeId({
    objName: StringId,
    objValue: _.identity
  }, "setObjectValue");







/******************************************************************/
/*                           EXPORTING                            */
/******************************************************************/



var ProcessInterpreter = {
  getFormReaction: getFormReaction,
  knownTypes: _.keys(exc),
  customInterpretation: {},
  parseProcess: parseProcess,
  trmap: {},
  lmap: {},
  tp: {}
};

_.concat(_.keys(exc), [
  "AddZeroElements",
  "Pass", "ShowText", "Title", "setHint", "Repeat", "Cond", "transformProcess"
]).forEach(function(name) {
  ProcessInterpreter[name] = window[name] = eval(name);
  // e.g. Scope["Seq"] = Seq
});



ProcessInterpreter.isProcessEmpty = isProcessEmpty;
ProcessInterpreter.simplifyProcess = simplifyProcess;
ProcessInterpreter.logState = logState;

ProcessInterpreter.ProcessId = ProcessId;

module.exports = ProcessInterpreter;
